// File: src/pages/PayrollDashboard.jsx

import React, { useEffect, useState } from 'react';
import axios from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import SmartPayrollTable from '@/components/Payroll/SmartPayrollTable';

const getCurrentISOWeek = () => {
  const now = new Date();
  const onejan = new Date(now.getFullYear(), 0, 1);
  const millisecsInDay = 86400000;
  const days = Math.floor((now - onejan) / millisecsInDay);
  const week = Math.ceil((now.getDay() + 1 + days) / 7);
  return `${now.getFullYear()}-W${week.toString().padStart(2, '0')}`;
};

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { canViewPayroll, loaded } = useUserPermissions();

  const [summary, setSummary] = useState({ totalStaff: 0, grossTotal: 0, departments: [] });
  const [week, setWeek] = useState(getCurrentISOWeek()); // ✅ default to current week
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loaded && !canViewPayroll) {
      navigate('/unauthorized');
    }
  }, [loaded, canViewPayroll, navigate]);

  useEffect(() => {
    if (loaded && canViewPayroll && week) {
      fetchPayrollSummary();
    }
  }, [week, loaded, canViewPayroll]);

  const fetchPayrollSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/solo-mdsh-api/staffing/payroll-smart/summary', {
        params: { week },
      });
      setSummary(res.data.summary);
      setApproved(res.data.isApproved);
    } catch (err) {
      console.error('Error fetching payroll summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalToggle = async () => {
    try {
      await axios.post('/solo-mdsh-api/staffing/payroll-smart/approve', {
        week,
        approve: !approved,
      });
      setApproved(!approved);
    } catch (err) {
      console.error('Approval toggle failed:', err);
    }
  };

  if (!loaded) return <div className="p-8 text-gray-600">🔄 Loading access...</div>;

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h1 className="text-3xl font-semibold text-gray-800">📊 Payroll Dashboard</h1>
        <div className="flex gap-3">
          <input
            type="week"
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 shadow-sm"
          />
          <Button
            onClick={() =>
              window.open(`/solo-mdsh-api/staffing/payroll-smart/export?format=csv&week=${week}`, '_blank')
            }
            variant="outline"
          >
            Export CSV
          </Button>
          <Button
            onClick={() =>
              window.open(`/solo-mdsh-api/staffing/payroll-smart/export?format=pdf&week=${week}`, '_blank')
            }
          >
            Export PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Staff" value={summary.totalStaff} />
        <SummaryCard label="Gross Pay Total" value={`£${summary.grossTotal?.toFixed(2)}`} color="text-green-600" />
        <div className="bg-white border rounded p-4 shadow-sm col-span-2">
          <p className="text-gray-500 font-medium mb-2">Cost Centre Breakdown</p>
          <ul className="text-sm text-gray-700 space-y-1 max-h-32 overflow-auto">
            {summary.departments?.map((dept, i) => (
              <li key={i} className="flex justify-between">
                <span>{dept.name}</span>
                <span className="font-semibold">£{dept.total.toFixed(2)}</span>
              </li>
            ))}
            {summary.departments?.length === 0 && (
              <li className="text-gray-400 italic">No breakdown data</li>
            )}
          </ul>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600 font-medium">
          Payroll Status:{' '}
          {approved ? (
            <span className="text-green-600 font-semibold">Approved</span>
          ) : (
            <span className="text-yellow-600 font-semibold">Pending</span>
          )}
        </p>
        <Button
          className={approved ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
          onClick={handleApprovalToggle}
        >
          {approved ? 'Reopen Payroll' : 'Approve Payroll'}
        </Button>
      </div>

      <SmartPayrollTable week={week} />
    </div>
  );
};

const SummaryCard = ({ label, value, color = 'text-gray-900' }) => (
  <div className="bg-white border rounded p-4 shadow-sm">
    <p className="text-gray-500 font-medium">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

export default PayrollDashboard;
